/* ============================================================
   Premium cart drawer behavior.
   - Countdown timer (sessionStorage-persisted)
   - Reward bar sync + multi-tier reconciliation (auto gift / auto discount)
   - Footer bits (count, savings, checkout amount) kept in sync
   - Discount accordion, gift wrap, subscribe nudge, continue/close
   Attaches via event delegation on document + a MutationObserver on
   .side-cart-inner, so it survives the theme's server re-renders without
   editing theme.js. /cart.js is the single source of truth.
   ============================================================ */
(function () {
	'use strict';

	var BUSY = false;          // reconciliation / mutation lock
	var lastCrossed = 0;       // number of tiers met (for flash + boundary re-render)
	var lastRewardEarned = false; // free-shaker PRODUCT node earned? tracked separately from lastCrossed (that node is deliberately kept out of the .mk.hit count) so it gets its own confetti — see applyDrawerHtml
	var observer = null;
	var syncTimer = null;
	var syncQueuedAt = 0;      // start of the current debounce window (bounds rapid-burst starvation)
	var timerInterval = null;
	var proofInterval = null;   // rotating social-proof ticker
	var appliedDiscountCodes = [];   // applicable codes from /cart.js — carried into checkout URL
	var lastCart = null;             // most recent /cart.js snapshot (for optimistic subtotal repaint)
	var lastChipsEl = null;          // discount-chip container last rendered into (idempotent guard)
	var lastChipsSig = null;         // HTML signature last written to the chips (kills re-animation blink)
	var lastSaved = null;            // last "total saved" cents painted (for the count-up animation)
	/* Free-gift state is now PER-RULE — keyed by the gift's reward variant id (its "key") so multiple gift
	   rules (creatine→shaker, curcumin→X, …) each manage their own arm/timer/spinner independently. */
	var puGiftFailed = {};           // key -> variant id whose /cart/add.js failed (don't re-attempt this session)
	var puGiftArm = {};              // key -> gift "add up" is armed (manual: shopper tapped Add). AUTO gifts add immediately (no arm).
	var puGiftTimer = {};            // key -> (legacy) auto-add delay timer. Auto gifts now add instantly, so this stays empty; the clears below are harmless no-ops kept as a safety net.
	var puWasDone = {};              // key -> was the "unlocked" gift card already on screen? (one-shot pop guard)
	var puGiftClaiming = {};         // key -> a manual "Add" claim is in flight (drive the button spinner from render)
	/* Discount-protection safeguard: a gift that, once added, is detected to strip a customer discount is
	   blocked from re-adding for the rest of the session (Shopify voids a product's other discounts when it
	   becomes part of a Buy-X-Get-Y — so an overlapping/mis-set free gift can silently eat the shopper's own
	   discount). Normally EMPTY: with the gift's 100%-off set as "amount off a DIFFERENT product" both stack. */
	var puGiftConflict = {};         // reward variant id -> free gift blocked (it evicted a customer discount)
	var tierGiftConflict = {};       // tier n (string) -> reward-tier gift blocked (it evicted a customer discount)
	function anyGiftArmed() { for (var k in puGiftArm) { if (puGiftArm[k]) { return true; } } return false; }
	/* Gift rules from the [data-np-upsells] JSON — accepts the new `gifts` array OR a legacy single `gift`. */
	function readGiftRules() {
		var pu = readUpsells();
		if (!pu) { return []; }
		var arr = Array.isArray(pu.gifts) ? pu.gifts : (pu.gift ? [pu.gift] : []);
		return arr.filter(function (g) { return g && g.enabled && g.variant_id && ((g.trigger_ids && g.trigger_ids.length) || (g.trigger_variant_ids && g.trigger_variant_ids.length)); });
	}
	var savedRAF = null;             // in-flight count-up animation frame

	/* ---------- helpers ---------- */
	/* Swap the store's currency token ("Rs."/"INR") for the ₹ glyph, keeping the store's exact
	   grouping + decimals. money() feeds ONLY the drawer's totals (subtotal, discount, savings,
	   checkout amount, reward bar) — cart line-item prices stay theme-rendered as "Rs." per spec. */
	function toRupee(s) {
		return String(s).replace(/(?:Rs\.?|INR|₹)\s*/i, '₹');
	}
	function money(cents) {
		var s = null;
		if (typeof window.btyFormatPrice === 'function') { try { s = window.btyFormatPrice(cents); } catch (e) {} }
		if (s == null && window.Shopify && Shopify.formatMoney && window.btyGlobals && btyGlobals.money_format) {
			try { s = Shopify.formatMoney(cents, btyGlobals.money_format); } catch (e) {}
		}
		if (s == null) { s = '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
		return toRupee(s);
	}

	function readTiers() {
		var el = document.querySelector('[data-cart-tiers]');
		if (!el) { return []; }
		try {
			var arr = JSON.parse(el.textContent || '[]');
			return arr.sort(function (a, b) { return a.threshold_cents - b.threshold_cents; });
		} catch (e) { return []; }
	}

	/* tailored product-upsell config (rendered by cart-drawer-upsells.liquid) — drives the free-gift auto-add */
	function readUpsells() {
		var el = document.querySelector('.side-cart [data-np-upsells]');
		if (!el) { return null; }
		try { return JSON.parse(el.textContent || '{}'); } catch (e) { return null; }
	}
	/* Runs after every drawer paint. (1) One-shot checkmark pop: add .pu-anim only on the absent→present
	   transition so unrelated re-renders don't restart the CSS animation. (2) Drive the manual "Add" spinner
	   FROM THE RENDER — the button node is recreated on each repaint, so a tap-closure can't track it: while a
	   claim is in flight, keep the spinner on the freshly-rendered button; clear it when the card is gone (gift
	   landed) or flash an error if the variant turned out blocked (sold out / unpublished). */
	function syncGiftPop() {
		/* one entry per on-screen "unlocked" card, keyed by its data-gift-key (the reward variant id) */
		var cards = document.querySelectorAll('.side-cart .pu-gift.is-done');
		var seen = {};
		cards.forEach(function (card) {
			var key = card.getAttribute('data-gift-key') || '1';
			seen[key] = true;
			if (!puWasDone[key]) { card.classList.add('pu-anim'); }   // one-shot pop only on absent→present
			puWasDone[key] = true;
			if (!puGiftClaiming[key]) { return; }
			var btn = card.querySelector('[data-pu-gift-add]');
			if (!btn) { return; }
			if (puGiftArm[key]) { btn.classList.add('loading'); return; }   // still pending (BUSY-deferred)
			/* arm consumed but the card is still here → the claim ran without landing: stop spinning, and flash
			   the error if the variant turned out blocked (sold out / unpublished). */
			puGiftClaiming[key] = false;
			btn.classList.remove('loading');
			if (puGiftFailed[key] != null) {
				btn.classList.add('pu-add-err');
				setTimeout(function () { var b = card.querySelector('[data-pu-gift-add]'); if (b) { b.classList.remove('pu-add-err'); } }, 2200);
			}
		});
		/* a card that disappeared → that rule's gift landed: reset its pop guard + clear any lingering claim spinner */
		for (var k in puWasDone) { if (!seen[k]) { puWasDone[k] = false; puGiftClaiming[k] = false; } }
	}
	/* the auto-added free-gift line (distinct property from reward-tier gifts so the two never collide) */
	function puGiftLine(cart) {
		return (cart.items || []).filter(function (it) { return it.properties && it.properties._np_pugift; })[0];
	}

	/* Optimistically hide a managed gift line that momentarily renders as PAID (no .gift-pill). When the
	   shopper drops a creatine below a full pair, the theme re-renders the drawer with the gift still at the
	   old qty while the Shopify discount now covers fewer units — Shopify SPLITS the line into a free chunk +
	   a charged chunk, and the reconciler is ~1s behind on removing the charged one. We run this from the
	   MutationObserver (a microtask, BEFORE the browser paints) so the charged unit never flashes; the free
	   chunk keeps its .gift-pill and stays visible, and the upsell card already shows "add 1 more". Safety:
	   if a gift stays paid past ~5s (a genuinely stuck charge — e.g. the auto-discount misfired) we reveal it
	   so the shopper is never silently charged for a hidden "free" item. */
	var giftPaidSince = 0;
	function hidePendingPaidGift() {
		var gifts = document.querySelectorAll('.side-cart .product-item[data-reward-gift]');
		var anyPaid = false;
		gifts.forEach(function (el) { if (!el.querySelector('.gift-pill')) { anyPaid = true; } });
		if (!anyPaid) { giftPaidSince = 0; return; }
		if (!giftPaidSince) { giftPaidSince = Date.now(); }
		var stuck = (Date.now() - giftPaidSince) > 5000;   /* reconciler normally clears it in ~1s */
		gifts.forEach(function (el) {
			if (el.querySelector('.gift-pill')) { return; }              /* genuinely free → leave visible */
			if (stuck) { el.classList.remove('gift-pending-remove'); }   /* safety: surface a stuck charge */
			else { el.classList.add('gift-pending-remove'); }
		});
	}

	function getCart() {
		/* no-store: never let a cached /cart.js repaint the header count/reward bar with a stale value AFTER the theme's
		   synchronous btyCartItemCount already wrote the fresh count (matches rerenderDrawer's fetch discipline). */
		return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' }).then(function (r) { return r.json(); });
	}

	function giftLines(cart) {
		/* both reward-tier gifts and tailored free gifts: excluded from reward eligibility + savings math
		   so a not-yet-zeroed gift line can't inflate the total. (reconcileTiers still matches reward gifts
		   by the _reward_tier VALUE, so including _np_pugift here doesn't affect tier matching.) */
		return (cart.items || []).filter(function (it) { return it.properties && (it.properties._reward_tier || it.properties._np_pugift); });
	}

	/* What the shopper pays for their OWN merchandise = cart total minus what they pay for gift lines (a
	   zeroed gift contributes 0). We compare this before/after a gift add: if it goes UP, the gift made one of
	   the shopper's products ineligible for its discount (Shopify's Buy-X-Get-Y rule) so they'd pay more — and
	   we roll the gift back out. Using total_price (which already nets EVERY discount) instead of attributing
	   discounts to lines makes this immune to Shopify re-allocating an order/fixed-amount/pooled discount onto
	   the free-gift line: such reshuffling never moves total_price, so it can never false-trigger a rollback. */
	function customerPayableCents(cart) {
		var giftPaid = giftLines(cart).reduce(function (s, it) { return s + (it.final_line_price || 0); }, 0);
		return (cart.total_price || 0) - giftPaid;
	}
	/* A signature of the shopper's OWN cart (non-gift line variant:qty composition + applied discount codes),
	   used only to detect whether the shopper changed anything of THEIRS during the async gift-add window. A
	   pure gift add never moves it, so if it differs post-add the payable change can't be pinned on the gift
	   and we skip the rollback (fail toward keeping both the gift AND whatever the shopper just did). Catches
	   quantity edits, equal-value swaps, and in-window discount-code add/remove that a price-sum alone misses. */
	function ownMerchSig(cart) {
		var parts = [];
		(cart.items || []).forEach(function (it) {
			if (it.properties && (it.properties._reward_tier || it.properties._np_pugift)) { return; }
			parts.push(String(it.variant_id) + ':' + it.quantity);
		});
		parts.sort();
		var codes = (cart.discount_codes || []).filter(function (d) { return d.applicable; })
			.map(function (d) { return String(d.code).toUpperCase(); }).sort();
		return parts.join(',') + '|' + codes.join(',');
	}

	function eligibleCents(cart) {
		var gift = giftLines(cart).reduce(function (s, it) { return s + it.final_line_price; }, 0);
		/* Measure reward progress against the merchandise SUBTOTAL (before the tier discounts), NOT total_price.
		   total_price is post-discount, so unlocking a 5%/10% tier lowers it, which drops eligible back below the
		   threshold — the bar then reads the discounted value and shows "add ₹X more" even though the subtotal
		   already qualified (the exact bug reported). items_subtotal_price is after line-level discounts (a free
		   gift is already 0) but BEFORE order-level discounts (the auto-applied tier codes), so it stays stable. */
		var base = (typeof cart.items_subtotal_price === 'number') ? cart.items_subtotal_price : (cart.total_price || 0);
		return base - gift;
	}

	/* Run a DOM-writing function without our own MutationObserver re-firing. */
	function withoutObserver(fn) {
		var inner = document.querySelector('.side-cart-inner');
		if (observer) { observer.disconnect(); }
		try { fn(); }
		finally { if (observer && inner) { observer.observe(inner, { childList: true, subtree: true }); } }
	}

	/* ---------- reward bar (cheap in-place update) ---------- */
	function renderRewardBar(elig, tiers) {
		var root = document.getElementById('cart-reward');
		if (!root || !tiers.length) { return; }
		var max = tiers[tiers.length - 1].threshold_cents || 0;
		var fill = root.querySelector('.fill');
		if (fill) { fill.style.width = Math.min(100, max ? (elig / max * 100) : 0) + '%'; }
		tiers.forEach(function (t) {
			var mk = root.querySelector('.mk[data-tier="' + t.n + '"]');
			var lb = root.querySelector('.labels span[data-tier="' + t.n + '"]');
			var am = root.querySelector('.amounts span[data-tier="' + t.n + '"]');
			var hit = elig >= t.threshold_cents;
			if (mk) { mk.classList.toggle('hit', hit); }
			if (lb) { lb.classList.toggle('hit', hit); }
			if (am) { am.classList.toggle('hit', hit); }
		});
		var msg = root.querySelector('[data-reward-msg]');
		var span = msg ? msg.querySelector('span') : null;
		var next = tiers.filter(function (t) { return elig < t.threshold_cents; })[0];
		if (next) {
			if (msg) { msg.classList.remove('s1'); }
			if (span) { span.innerHTML = String(next.pre).replace('[remaining]', '<b>' + money(next.threshold_cents - elig) + '</b>'); }
		} else {
			var top = tiers[tiers.length - 1];
			if (msg) { msg.classList.add('s1'); }
			if (span) { span.innerHTML = top.unlocked; }
		}
	}

	/* ---------- Free-Shaker reward node: INSTANT (JS) light + confetti, at spend-marker speed ----------
	   The product-gated node used to update ONLY on the server re-render (applyDrawerHtml) — a beat slower than
	   spend markers, which renderRewardBar() toggles instantly from /cart.js. This mirrors the reconciler's
	   cumulative count (reconcileGift) EXACTLY from the live cart, toggles .rw-hit on the node + label right
	   away, and returns the earned state so syncRewards can fire the same confetti the spend tiers get. */
	function syncRewardNode(cart) {
		var node = document.querySelector('#cart-reward [data-reward-gift-mk]');
		if (!node) { return null; }                         /* node not shown (no trigger product in cart) */
		var rules = readGiftRules(), g = null;
		for (var i = 0; i < rules.length; i++) { if (rules[i].mode === 'cumulative') { g = rules[i]; break; } }
		if (!g) { return null; }                            /* the bar node maps to the CUMULATIVE reward offer */
		var trigSet = {}, vtrigSet = {};
		(g.trigger_ids || []).forEach(function (id) { trigSet[String(id)] = true; });
		(g.trigger_variant_ids || []).forEach(function (id) { vtrigSet[String(id)] = true; });
		var need = parseInt(g.qty, 10) || 2, total = 0;
		(cart.items || []).forEach(function (it) {
			if (it.properties && (it.properties._np_pugift || it.properties._reward_tier)) { return; }  /* never count managed gifts */
			if (trigSet[String(it.product_id)] || vtrigSet[String(it.variant_id)]) { total += it.quantity; }
		});
		var earned = total >= need;                         /* cumulative: total >= need (mirrors Liquid rw_earned) */
		node.classList.toggle('rw-hit', earned);
		var lbl = document.querySelector('#cart-reward [data-reward-gift-lbl]');
		if (lbl) { lbl.classList.toggle('rw-hit', earned); }
		return earned;
	}

	/* Paint the header cart-icon badge (.cart-item-count + .item-count-label) from the live count.
	   We write it directly (NOT via the theme's btyCartItemCount, which adds .hidden at 0) so the badge
	   always reads the number — including "0" when the cart empties — instead of disappearing. The CSS
	   override in cart-drawer.css keeps it visible the instant it hits 0 (no flash). */
	function syncHeaderCount(count) {
		var n = Number(count) || 0;
		document.querySelectorAll('.cart-item-count').forEach(function (el) {
			if (el.textContent !== String(n)) { el.textContent = String(n); }
			el.classList.remove('hidden');   /* show "0" rather than hiding the badge when empty */
		});
		var labelOne = (window.cartText && window.cartText.one) || 'item';
		var labelOther = (window.cartText && window.cartText.other) || 'items';
		var label = n === 1 ? labelOne : labelOther;
		document.querySelectorAll('.item-count-label').forEach(function (el) {
			if (el.textContent !== label) { el.textContent = label; }
		});
	}

	/* ---------- footer bits ---------- */
	function updateFooterBits(cart) {
		var cnt = document.querySelector('.side-cart [data-cart-count]');
		if (cnt) { cnt.textContent = cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items'); }

		/* Keep the HEADER cart-icon badge in sync. /cart.js is the single source of truth, and
		   syncRewards() runs after EVERY mutation — including the ones cart-drawer.js performs
		   directly (free-gift auto-add, tier reconcile, discount apply) that never pass through
		   theme.js's btyCartItemCount(). Without this, those mutations leave the navbar count
		   stale until a page refresh. Idempotent: it just writes the authoritative count. */
		syncHeaderCount(cart.item_count);

		/* "You've saved so far" = compare-at markdowns + all applied discounts (codes + automatic) */
		var savings = 0;
		(cart.items || []).forEach(function (it) {
			/* Skip BOTH gift types — a gift's real value is already in total_discount, so counting its
			   catalog markdown on top is phantom double-saving. Without _np_pugift here, the free shaker's
			   compare-at markdown inflated "Saved so far" after the first JS sync (the Liquid already
			   excludes both — see the footer total_savings loop in side-cart.liquid). */
			if (it.properties && (it.properties._reward_tier || it.properties._np_pugift)) { return; }
			var cmp = it.compare_at_price || 0;
			if (cmp > it.price) { savings += (cmp - it.price) * it.quantity; }
		});
		var totalSaved = savings + (cart.total_discount || 0);
		var amtEl = document.querySelector('.side-cart [data-saved-amt]');
		var bar = document.querySelector('.side-cart [data-saved-bar]');
		if (amtEl) {
			var from = (lastSaved != null) ? lastSaved : parseInt(amtEl.getAttribute('data-saved-cents') || '0', 10);
			var changed = from !== totalSaved;
			lastSaved = totalSaved;
			amtEl.setAttribute('data-saved-cents', String(totalSaved));
			animateSaved(amtEl, from, totalSaved);
			if (bar) {
				bar.hidden = totalSaved <= 0;
				if (changed && totalSaved > 0) { bumpSaved(bar); }
			}
		}

		document.querySelectorAll('.side-cart .co-amt').forEach(function (el) { el.textContent = money(cart.total_price); });
	}

	/* count-up the saved amount from `from`→`to` cents, formatting each frame with money() */
	function animateSaved(el, from, to) {
		if (savedRAF) { cancelAnimationFrame(savedRAF); savedRAF = null; }
		var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		if (from === to || reduce) { el.textContent = money(to); return; }
		var dur = 650, start = null;
		function step(ts) {
			if (start == null) { start = ts; }
			var p = Math.min(1, (ts - start) / dur);
			var eased = 1 - Math.pow(1 - p, 3);   /* easeOutCubic */
			el.textContent = money(Math.round(from + (to - from) * eased));
			if (p < 1) { savedRAF = requestAnimationFrame(step); }
			else { el.textContent = money(to); savedRAF = null; }
		}
		savedRAF = requestAnimationFrame(step);
	}
	/* restart the pop animation on the banner */
	function bumpSaved(bar) {
		bar.classList.remove('bumped');
		void bar.offsetWidth;
		bar.classList.add('bumped');
	}

	/* Swap fresh server-rendered side-cart markup into the live drawer + re-init theme hooks. */
	function applyDrawerHtml(html) {
		if (!html) { return false; }
		var doc = new DOMParser().parseFromString(html, 'text/html');
		var fresh = doc.querySelector('.side-cart-inner');
		var cur = document.querySelector('.side-cart-inner');
		if (!fresh || !cur) { return false; }
		/* Preserve the scroll position of the scrollable body across the innerHTML swap so a re-render
		   (item remove, qty change, discount) doesn't bounce the shopper back to the top. */
		var oldBody = cur.querySelector('.side-cart-content');
		var savedScroll = oldBody ? oldBody.scrollTop : 0;
		/* Capture the reward bar's current fill width BEFORE the swap. The server renders .fill at its final
		   width, so a plain innerHTML swap makes the milestone bar JUMP to the goal. We FLIP it after the swap
		   (snap back to this old width, then transition to the new) so an upsell add animates smoothly. */
		var oldRewardFill = cur.querySelector('#cart-reward .fill');
		var oldRewardW = oldRewardFill ? oldRewardFill.style.width : '';
		/* Preserve already-LOADED recommendation blocks — the "Frequently bought together" upsell and the
		   "you may also like" carousel — across the swap so a cart-count change doesn't respawn / re-fetch
		   them (the flicker). We keep the live node only when it actually holds loaded cards; an unloaded
		   one falls through to the fresh server markup so it can load normally the first time. */
		var keepRecs = [];
		['.minicart-recommendations-wrapper', '[data-cart-carousel]'].forEach(function (sel) {
			var el = cur.querySelector(sel);
			/* preserve only when REAL cards are loaded (skeleton placeholders must fall through to load) */
			if (el && el.querySelector('.fbt-card:not(.fbt-card--skel), .cc-card:not(.cc-card--skel)')) { keepRecs.push({ sel: sel, node: el }); }
		});
		withoutObserver(function () {
			cur.innerHTML = fresh.innerHTML;
			/* re-seed the saved-bar from the live JS value so the (lower) server value never paints */
			if (lastSaved != null) {
				var sAmt = cur.querySelector('[data-saved-amt]');
				var sBar = cur.querySelector('[data-saved-bar]');
				if (sAmt) { sAmt.setAttribute('data-saved-cents', String(lastSaved)); sAmt.textContent = money(lastSaved); }
				if (sBar) { sBar.hidden = lastSaved <= 0; }
			}
			/* drop the preserved recommendation nodes back in over the fresh (re-rendered / empty) versions */
			keepRecs.forEach(function (k) {
				var freshEl = cur.querySelector(k.sel);
				if (freshEl && freshEl.parentNode) { freshEl.parentNode.replaceChild(k.node, freshEl); }
			});
			/* restore scroll synchronously on the fresh body (clamps itself if the content got shorter) */
			if (savedScroll) { var newBody = cur.querySelector('.side-cart-content'); if (newBody) { newBody.scrollTop = savedScroll; } }
			/* FLIP the reward bar: snap the new fill back to the old width, force a reflow, then let it transition
			   to the server-rendered target — turns the milestone "jump" into a smooth fill on add/remove. */
			var nf = cur.querySelector('#cart-reward .fill');
			if (nf && oldRewardW) {
				var tgt = nf.style.width;
				if (tgt && tgt !== oldRewardW) {
					nf.style.transition = 'none';
					nf.style.width = oldRewardW;
					void nf.offsetWidth;
					nf.style.transition = '';
					nf.style.width = tgt;
				}
			}
		});
		if (window.btyAddToCart) { try { btyAddToCart(); } catch (e) {} }
		if (window.btyUpdateProductQuantity) { try { btyUpdateProductQuantity(); } catch (e) {} }
		if (window.btyQuantityButton) { try { btyQuantityButton(); } catch (e) {} }
		if (window.btyMinicartRecommendations) { try { btyMinicartRecommendations(); } catch (e) {} }
		if (window.btySideCartPopupOuter) { try { btySideCartPopupOuter(); } catch (e) {} }
		noteInit();
		loadCarousel();
		syncGiftPop();
		/* ---- Free-Shaker (product-gated reward) confetti ----
		   The reward node lights via SERVER HTML only — renderRewardBar leaves it alone (no data-tier) — so its
		   earned state flips exactly HERE, on the innerHTML swap. Celebrate the same way spend tiers do when it
		   goes un-earned → earned. Tracked with its OWN flag + the `.rw-hit` class (NOT .mk.hit / lastCrossed):
		   the node is intentionally excluded from the spend-tier count, and this keeps that intact while still
		   giving it a celebration. fireConfetti() self-guards on side-cart-open + reduced-motion. */
		var rwEarnedNow = !!document.querySelector('#cart-reward [data-reward-gift-mk].rw-hit');
		if (rwEarnedNow && !lastRewardEarned && document.documentElement.classList.contains('side-cart-open')) {
			flashCheckout();
			fireConfetti();
		}
		lastRewardEarned = rwEarnedNow;
		return true;
	}

	/* ---------- full server re-render (fixes message/icon, gift line) ---------- */
	function rerenderDrawer() {
		var u = window.location.pathname;
		/* GET section renders can be served STALE from Shopify's CDN (a cart mutated just before this fetch may
		   not be reflected yet). Bust the cache with a unique param + no-store so this never repaints an old cart. */
		var bust = (u.indexOf('?') > -1 ? '&' : '?') + 'sections=side-cart&_=' + Date.now();
		return fetch(u + bust, { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
			.then(function (r) { return r.json(); })
			.then(function (j) { applyDrawerHtml(j && j['side-cart']); })
			.catch(function () {});
	}

	/* Reconcile ONE free-gift rule into `ops`. PER-PRODUCT target: 1 gift per trigger PRODUCT that reaches `need`
	   units, capped at 1 (3/4/10 of the same trigger is still 1). Matched to THIS rule's gift lines only — tagged
	   _np_pugift AND of the reward variant `key` — so rules with different rewards never touch each other's lines,
	   and a partial-discount split into >1 line is summed and re-consolidated. All arm/timer/blocked state keyed
	   by `key` (the reward variant id). Mirrors the single-gift logic exactly; only the state is now per-rule. */
	function reconcileGift(g, key, cart, ops) {
		var trigSet = {}, vtrigSet = {};
		(g.trigger_ids || []).forEach(function (id) { trigSet[String(id)] = true; });
		(g.trigger_variant_ids || []).forEach(function (id) { vtrigSet[String(id)] = true; });
		var need = parseInt(g.qty, 10) || 2;
		var cumulative = (g.mode === 'cumulative');
		var perProduct = {}, total = 0;
		(cart.items || []).forEach(function (it) {
			if (it.properties && (it.properties._np_pugift || it.properties._reward_tier)) { return; }   /* never count managed gifts */
			if (trigSet[String(it.product_id)] || vtrigSet[String(it.variant_id)]) {   /* match by product (any variant) OR by specific variant */
				perProduct[it.product_id] = (perProduct[it.product_id] || 0) + it.quantity;
				total += it.quantity;
			}
		});
		var target = 0;
		if (cumulative) {
			target = Math.floor(total / need);     /* cumulative: total across ALL triggers ÷ need */
		} else {
			Object.keys(perProduct).forEach(function (pid) { if (perProduct[pid] >= need) { target += 1; } });   /* per-product: each product reaching need */
		}
		var lines = (cart.items || []).filter(function (it) { return it.properties && it.properties._np_pugift && String(it.variant_id) === key; });
		var current = lines.reduce(function (s, l) { return s + l.quantity; }, 0);
		var blocked = puGiftFailed[key] != null || puGiftConflict[key] === true;   /* sold-out/invalid, OR it evicted a customer discount */
		if (puGiftTimer[key] && target <= current) { clearTimeout(puGiftTimer[key]); puGiftTimer[key] = null; }
		var normalize = function (qty) {
			if (qty <= 0) { lines.forEach(function (l) { ops.push({ remove: { key: l.key } }); }); return; }
			ops.push({ setqty: { key: lines[0].key, quantity: qty } });
			for (var i = 1; i < lines.length; i++) { ops.push({ remove: { key: lines[i].key } }); }
		};
		if (target < current) {
			normalize(target);                  /* dropped below earned (or a split stranded a charged unit) → pull down */
			puGiftArm[key] = false;
		} else if (target > current && !blocked) {
			/* earned more than in cart. AUTO gifts add IMMEDIATELY — the instant the milestone is reached, in THIS
			   same reconcile pass (reconcileTiers only calls us when !BUSY, so the pushed op always runs). MANUAL
			   gifts still add only once the shopper taps "Add" (which sets puGiftArm). The old 1s auto-arm timer +
			   its extra sync round-trip is gone — that was the 3-4s lag before the free shaker landed in the cart. */
			if (puGiftArm[key] || g.auto) {
				puGiftArm[key] = false;
				if (puGiftTimer[key]) { clearTimeout(puGiftTimer[key]); puGiftTimer[key] = null; }
				if (lines.length) { normalize(target); }
				else { ops.push({ add: { id: g.variant_id, quantity: target, props: { _np_pugift: '1' }, gift: true, giftKey: key } }); }
			}
		} else {
			puGiftArm[key] = false;             /* target === current: nothing owed */
			if (lines.length > 1) { normalize(target); }   /* defensive: consolidate a stray split */
		}
	}

	/* ---------- tier reconciliation (auto gift / auto discount) ---------- */
	function reconcileTiers(cart, elig) {
		if (BUSY) { return Promise.resolve(false); }
		var tiers = readTiers();
		var gifts = giftLines(cart);
		var ops = [];

		tiers.forEach(function (t) {
			if (t.type === 'gift' && t.gift_variant_id) {
				var line = gifts.filter(function (g) { return String(g.properties._reward_tier) === String(t.n); })[0];
				var present = !!line;
				if (elig >= t.threshold_cents && !present && tierGiftConflict[String(t.n)] !== true) {
					ops.push({ add: { id: t.gift_variant_id, props: { _reward_tier: String(t.n) } } });
				} else if (elig < t.threshold_cents && present) {
					ops.push({ remove: { key: line.key } });
				}
			} else if (t.type === 'discount' && t.discount_code) {
				var dk = 'cart_discount_' + t.n;
				if (elig >= t.threshold_cents && !sessionStorage.getItem(dk)) {
					ops.push({ discount: t.discount_code, key: dk });
				}
			}
		});

		/* ① tailored free-gifts (e.g. 2× creatine → free shaker). MULTI-RULE: each gift rule manages its OWN gift
		   line(s) + per-rule arm/timer, all reusing this add/remove engine + BUSY lock (see reconcileGift). */
		var giftRules = readGiftRules();
		var giftActive = {};
		giftRules.forEach(function (g) {
			var gkey = String(g.variant_id);
			giftActive[gkey] = true;
			reconcileGift(g, gkey, cart, ops);
		});
		/* drop stale arm/timer for any rule no longer active (config changed / trigger gone) so a sync can't spin */
		for (var gk in puGiftArm) { if (!giftActive[gk]) { puGiftArm[gk] = false; } }
		for (var tk in puGiftTimer) { if (!giftActive[tk] && puGiftTimer[tk]) { clearTimeout(puGiftTimer[tk]); puGiftTimer[tk] = null; } }

		/* ② orphan sweep — remove a managed free-gift line (_np_pugift) that NO active rule covers anymore.
		   reconcileGift only ever touches lines of a rule that's still in [data-np-upsells], and that JSON drops
		   a reward's rule the instant its trigger leaves the cart (cart-drawer-upsells only emits it while the
		   trigger is present). So pulling all trigger units at once orphans the gift: reconcileGift never sees it,
		   it lingers in the cart, and once its Buy-X-Get-Y auto-discount stops applying it becomes a CHARGED line
		   (hidePendingPaidGift only hides it for ~5s). Sweep it here. Fail-safe guards:
		     (a) only when the upsell config actually rendered ([data-np-upsells] present) — a transient
		         section-render miss then leaves gifts untouched instead of nuking a genuinely-earned one;
		     (b) skip a variant an active rule manages (reconcileGift owns its add/keep/remove);
		     (c) skip a variant mid-add this session (armed / timer pending) so we never yank one about to land. */
		if (document.querySelector('.side-cart [data-np-upsells]')) {
			(cart.items || []).forEach(function (it) {
				if (!it.properties || !it.properties._np_pugift) { return; }
				var vk = String(it.variant_id);
				if (giftActive[vk] || puGiftArm[vk] || puGiftTimer[vk]) { return; }
				ops.push({ remove: { key: it.key } });
			});
		}

		if (!ops.length) { return Promise.resolve(false); }

		/* ---- discount-protection baseline (see puGiftConflict) ----
		   Snapshot what the shopper pays for their OWN merchandise (+ its pre-discount value), and note any
		   gift ADD in this batch, BEFORE mutating. The reconciler only ever adds/removes gift lines, so if the
		   shopper's own payable RISES after a gift add — with their own merchandise otherwise unchanged — the
		   gift made a product ineligible for its discount. We verify + roll back below. With the gift's 100%-off
		   set as "amount off a DIFFERENT product" this never fires (both discounts stack). */
		var prePay = customerPayableCents(cart);
		var preSig = ownMerchSig(cart);
		var giftAdds = [];
		ops.forEach(function (op) {
			if (op.add && op.add.props && (op.add.props._np_pugift || op.add.props._reward_tier)) {
				giftAdds.push({ id: String(op.add.id), tier: op.add.props._reward_tier ? String(op.add.props._reward_tier) : null });
			}
		});

		BUSY = true;
		var chain = Promise.resolve();
		/* Capture the freshly-rendered drawer from each cart mutation's OWN response. Cart API `sections` are
		   rendered POST-mutation and are never CDN-cached, so painting the LAST captured one makes an auto-added
		   gift (e.g. the free shaker) appear in the drawer instantly. The previous GET re-render could be served
		   the pre-op cart, so the gift only showed after a manual page refresh. */
		var freshHtml = null;
		var JH = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
		var secBody = function (extra) { extra.sections = ['side-cart']; extra.sections_url = window.location.pathname; return JSON.stringify(extra); };
		var capture = function (r) {
			if (!r || !r.ok) { return r; }
			return r.json().then(function (j) { if (j && j.sections && j.sections['side-cart']) { freshHtml = j.sections['side-cart']; } return j; });
		};
		ops.forEach(function (op) {
			chain = chain.then(function () {
				if (op.add) {
					return fetch('/cart/add.js', { method: 'POST', headers: JH, body: secBody({ items: [{ id: op.add.id, quantity: op.add.quantity || 1, properties: op.add.props || {} }] }) })
						.then(function (r) {
							/* A sold-out / invalid variant returns 422 (fetch resolves). Swallow ANY add failure to
							   null — never reject mid-chain — so the chain still reaches the paint below and the drawer
							   shows the real (post-attempt) cart instead of being left stale. For the free gift we also
							   flag the variant so we stop re-POSTing a guaranteed failure this session. */
							if (!r.ok) {
								if (op.add.gift) {
									puGiftFailed[op.add.giftKey] = op.add.id;
									/* Surface WHY a free gift refused to add — this is the "card says Unlocked but the
									   gift never lands in the cart" symptom. Almost always the gift product/variant is
									   out of stock (with tracking on) or unpublished from the Online Store channel. */
									r.clone().json().then(function (j) {
										console.warn('[Nutripeak] Free gift add FAILED — variant ' + op.add.id + ' → HTTP ' + r.status + (j && j.description ? (' — ' + j.description) : '') + '. Fix: make sure that gift product/variant is IN STOCK (or enable "Continue selling when out of stock") and PUBLISHED to the Online Store sales channel.');
									}).catch(function () {
										console.warn('[Nutripeak] Free gift add FAILED — variant ' + op.add.id + ' → HTTP ' + r.status + '. Fix: ensure the gift product/variant is in stock (or "continue selling when out of stock") and published to the Online Store sales channel.');
									});
								}
								return null;
							}
							return capture(r);
						});
				}
				if (op.remove) {
					return fetch('/cart/change.js', { method: 'POST', headers: JH, body: secBody({ id: op.remove.key, quantity: 0 }) }).then(capture);
				}
				if (op.setqty) {   /* adjust an existing gift line's quantity (repeating gift: 1 per `need`) */
					return fetch('/cart/change.js', { method: 'POST', headers: JH, body: secBody({ id: op.setqty.key, quantity: op.setqty.quantity }) }).then(capture);
				}
				if (op.discount) {
					sessionStorage.setItem(op.key, '1');
					return fetch('/discount/' + encodeURIComponent(op.discount), { method: 'GET' });
				}
			});
		});
		return chain
			/* ---- discount-protection verify + rollback ----
			   Only runs when this batch added a gift. Re-measure the shopper discount; if it fell ≥ ₹1, the
			   gift made a product ineligible for its discount → remove the gift line(s) we just added and block
			   them for the session, so the SHOPPER'S discount is the one that survives (never the reverse). The
			   rollback only ever removes gift lines, so it can never itself reduce a customer discount. */
			.then(function () {
				if (!giftAdds.length) { return; }
				/* Diagnostic / escape hatch: `window.__NP_GIFT_SAFEGUARD_OFF = true` in the console (then reload
				   and reproduce) skips the verify+rollback entirely, so the gift is always KEPT. Use it to confirm
				   whether this safeguard is what's removing a gift. Leaving it off is NOT recommended long-term —
				   it re-opens the "a gift can silently eat a customer discount" hole this guard exists to close. */
				if (window.__NP_GIFT_SAFEGUARD_OFF === true) { return; }
				return getCart().then(function (fresh) {
					if (ownMerchSig(fresh) !== preSig) { return; }                  /* shopper changed their OWN cart/codes mid-window → can't attribute to the gift */
					if (customerPayableCents(fresh) <= prePay + 100) { return; }     /* shopper pays no more for their own items → keep the gift(s) */
					/* We are about to drop a just-added free gift because keeping it would make the shopper pay
					   MORE for their own items — i.e. the gift's discount is NOT set to combine with the discount
					   already on the cart. This is almost always a Shopify "Combinations" setting, not a theme bug. */
					console.warn('[Nutripeak] Free gift removed to protect a customer discount: adding it raised the shopper\'s own payable from ₹' + (prePay / 100).toFixed(2) + ' to ₹' + (customerPayableCents(fresh) / 100).toFixed(2) + '. Fix: in Shopify → Discounts, enable "Combine with product discounts" on BOTH the gift discount (FREEPEAK) and the cart-item discount so they stack.', giftAdds);
					var keys = {};
					giftAdds.forEach(function (ga) {
						if (ga.tier) { tierGiftConflict[ga.tier] = true; }
						else {
							puGiftConflict[ga.id] = true; puGiftArm[ga.id] = false;
							if (puGiftTimer[ga.id]) { clearTimeout(puGiftTimer[ga.id]); puGiftTimer[ga.id] = null; }
						}
						(fresh.items || []).forEach(function (it) {
							if (!it.properties) { return; }
							var hit = ga.tier ? (String(it.properties._reward_tier) === ga.tier)
								: (it.properties._np_pugift && String(it.variant_id) === ga.id);
							if (hit) { keys[it.key] = true; }
						});
					});
					var rc = Promise.resolve();
					Object.keys(keys).forEach(function (k) {
						rc = rc.then(function () { return fetch('/cart/change.js', { method: 'POST', headers: JH, body: secBody({ id: k, quantity: 0 }) }).then(capture); });
					});
					return rc;
				}).catch(function () {});
			})
			/* Paint the captured post-mutation drawer; fall back to a (cache-busted) GET only if nothing was
			   captured. On a successful paint, resync lastCrossed from the freshly-rendered reward bar — parity
			   with puAdd/removeItem — so any later sync can't misread the boundary and fire a stale GET re-render. */
			.then(function () {
				if (applyDrawerHtml(freshHtml)) { lastCrossed = document.querySelectorAll('#cart-reward .mk.hit').length; }
				else { return rerenderDrawer(); }
			})
			.then(function () { return true; })
			.catch(function () { return true; })
			.finally(function () { BUSY = false; });
	}

	/* After a server re-render (rerenderDrawer) the subtotal + chips come back from server Liquid,
	   which can't see manual /cart/update.js codes — so the subtotal shows the UNDISCOUNTED total
	   and the chips are gone. Repaint the discount UI from fresh /cart.js to restore them. */
	function repaintDiscountsFresh() {
		return getCart().then(function (c) {
			lastCart = c;
			withoutObserver(function () { updateFooterBits(c); renderDiscounts(c); });
		}).catch(function () {});
	}

	/* ---------- atomic settle: mutation + gift, ONE paint ----------
	   Given a just-mutated `cart` and `pendingHtml` (that mutation's own side-cart render), reconcile the free
	   gift and paint the drawer EXACTLY ONCE with the final state. If the change earns/loses a gift, reconcileTiers
	   runs its op chain and paints count+gift together (from the gift op's own sections); otherwise we paint
	   `pendingHtml` (the count-only render). So the shaker is already in/out the instant the paint lands — no
	   second-paint pop-in. Fires spend-tier confetti in parity with syncRewards (reward-node confetti fires from
	   the applyDrawerHtml hook on the paint), then refreshes the discount chips. Callers hold their loader across
	   this, so "loader gone" == "gift settled". Reuses reconcileTiers → the discount-conflict safeguard is intact.
	   NOTE: reconcileTiers must run un-BUSY, so callers set BUSY=false before calling this. */
	function reconcileAndPaint(cart, pendingHtml) {
		var elig = eligibleCents(cart);
		var crossed = readTiers().filter(function (t) { return elig >= t.threshold_cents; }).length;
		var increased = crossed > lastCrossed;
		lastCrossed = crossed;
		return reconcileTiers(cart, elig).then(function (didOps) {
			if (!didOps) {
				if (pendingHtml) { if (!applyDrawerHtml(pendingHtml)) { return rerenderDrawer(); } }
				else { return rerenderDrawer(); }
			}
			/* didOps === true → reconcileTiers already painted the count+gift result */
		}).then(function () {
			if (increased) { flashCheckout(); fireConfetti(); }   /* spend-tier confetti; reward-node confetti fires in applyDrawerHtml */
			return repaintDiscountsFresh();
		}).catch(function () {});
	}

	/* ---------- main sync ---------- */
	function syncRewards() {
		syncQueuedAt = 0;   // a sync is starting — the next mutation opens a fresh debounce window
		return getCart().then(function (cart) {
			lastCart = cart;
			var tiers = readTiers();
			var elig = eligibleCents(cart);
			var crossed = tiers.filter(function (t) { return elig >= t.threshold_cents; }).length;
			var boundaryChanged = crossed !== lastCrossed;
			var increased = crossed > lastCrossed;
			lastCrossed = crossed;

			var rwEarned = null;
			withoutObserver(function () {
				updateFooterBits(cart);
				renderRewardBar(elig, tiers);
				rwEarned = syncRewardNode(cart);   /* instant-light the free-shaker node from the live cart (was server-only) */
				renderDiscounts(cart);
			});

			if (increased) { flashCheckout(); fireConfetti(); }
			/* Free-Shaker (product node) celebration on the SAME instant path spend tiers use. Shares
			   lastRewardEarned with the applyDrawerHtml backstop so it fires exactly once per un-earned→earned. */
			if (rwEarned !== null) {
				if (rwEarned && !lastRewardEarned && document.documentElement.classList.contains('side-cart-open')) {
					flashCheckout(); fireConfetti();
				}
				lastRewardEarned = rwEarned;
			}
			if (!timerInterval) { startTimer(); }

			return reconcileTiers(cart, elig).then(function (didOps) {
				/* A gift add stays ARMED if reconcileTiers had to early-return because the cart was BUSY. Nothing
				   else guarantees a follow-up (the BUSY op's finally may not sync), so re-schedule one here until
				   it lands. This can't spin: every non-BUSY reconcile pass either consumes the arm (add) or resets
				   it (target<=current, or the variant is blocked), so puGiftArm is only still true after a BUSY skip. */
				if (anyGiftArmed()) { scheduleSync(); }
				/* Both branches below run rerenderDrawer() (server Liquid = undiscounted, no chips);
				   follow each with a fresh repaint so an applied code still shows after a tier add/remove. */
				var tail;
				if (didOps) { tail = repaintDiscountsFresh(); }
				else if (boundaryChanged) { tail = rerenderDrawer().then(repaintDiscountsFresh); }
				/* reconcile the claim spinner/error even on a no-op sync (e.g. a blocked-variant re-tap that
				   pushes no ops and triggers no render) so the button can never be left spinning. */
				return Promise.resolve(tail).then(function () { syncGiftPop(); });
			});
		}).catch(function () {});
	}

	function scheduleSync() {
		var now = Date.now();
		if (!syncQueuedAt) { syncQueuedAt = now; }
		if (syncTimer) { clearTimeout(syncTimer); }
		/* ~120ms debounce to coalesce bursts, but never defer past ~450ms from the first pending
		   mutation — otherwise holding qty +/- would keep the optimistic subtotal showing a stale
		   (pre-change) figure for the whole burst. The cap forces a real /cart.js reconcile. */
		var wait = Math.min(120, Math.max(0, 450 - (now - syncQueuedAt)));
		syncTimer = setTimeout(syncRewards, wait);
	}

	/* ---------- checkout flash ---------- */
	function flashCheckout() {
		var f = document.querySelector('.side-cart .checkout .flash');
		if (!f) { return; }
		f.classList.remove('show');
		void f.offsetWidth;
		f.classList.add('show');
	}

	/* ---------- confetti celebration (self-contained, no deps) ----------
	   Fires when a NEW reward tier is unlocked. The canvas is appended to .side-cart (the scrim,
	   a sibling of the panel) — NOT .side-cart-inner — so a tier op's rerenderDrawer() innerHTML
	   swap can't wipe it mid-burst, and it sits outside the panel's MutationObserver subtree. */
	var confettiRAF = null;
	function fireConfetti() {
		if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { return; }
		if (!document.documentElement.classList.contains('side-cart-open')) { return; }
		var scrim = document.querySelector('.side-cart');
		var panel = document.querySelector('.side-cart .side-cart-inner');
		if (!scrim || !panel) { return; }
		var pr = panel.getBoundingClientRect();
		var W = Math.round(pr.width), H = Math.round(pr.height);
		if (!W || !H) { return; }

		/* palette from the live theme vars (with brand fallbacks) */
		var cs = getComputedStyle(scrim);
		function cv(name, fb) { var c = (cs.getPropertyValue(name) || '').trim(); return c || fb; }
		var colors = [cv('--cart-gold', '#c08830'), cv('--cart-terra', '#b85c3e'), cv('--cart-olive', '#6b7a3a'), cv('--cart-gold-soft', '#f6e4d0'), '#ffffff'];

		var dpr = Math.min(2, window.devicePixelRatio || 1);
		var canvas = scrim.querySelector('canvas.cart-confetti');
		if (!canvas) {
			canvas = document.createElement('canvas');
			canvas.className = 'cart-confetti';
			scrim.appendChild(canvas);
		}
		var sr = scrim.getBoundingClientRect();
		canvas.style.left = (pr.left - sr.left) + 'px';
		canvas.style.top = (pr.top - sr.top) + 'px';
		canvas.style.width = W + 'px';
		canvas.style.height = H + 'px';
		canvas.width = W * dpr;
		canvas.height = H * dpr;
		var ctx = canvas.getContext('2d');
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

		/* origin = the reward bar's centre (falls back to upper-third of the panel) */
		var ox = W / 2, oy = H * 0.2;
		var reward = panel.querySelector('#cart-reward');
		if (reward) {
			var rr = reward.getBoundingClientRect();
			ox = rr.left - pr.left + rr.width / 2;
			oy = rr.top - pr.top + rr.height / 2;
		}

		var N = W < 380 ? 80 : 120;
		var parts = [];
		for (var i = 0; i < N; i++) {
			var ang = (-Math.PI / 2) + (Math.random() - 0.5) * Math.PI * 1.15;   /* fan upward + out */
			var sp = 5 + Math.random() * 9.5;
			var shapeR = Math.random();
			parts.push({
				x: ox + (Math.random() - 0.5) * 46,
				y: oy + (Math.random() - 0.5) * 14,
				vx: Math.cos(ang) * sp,
				vy: Math.sin(ang) * sp - 2.5,
				g: 0.22 + Math.random() * 0.13,
				rot: Math.random() * Math.PI * 2,
				vr: (Math.random() - 0.5) * 0.42,
				size: 5 + Math.random() * 6,
				color: colors[(Math.random() * colors.length) | 0],
				shape: shapeR < 0.45 ? 'rect' : (shapeR < 0.72 ? 'ribbon' : 'circle'),
				life: 0,
				ttl: 86 + (Math.random() * 46 | 0)
			});
		}

		function frame() {
			ctx.clearRect(0, 0, W, H);
			var alive = 0;
			for (var i = 0; i < parts.length; i++) {
				var p = parts[i];
				if (p.life > p.ttl) { continue; }
				alive++;
				p.life++;
				p.vy += p.g;
				p.vx *= 0.99;
				p.x += p.vx;
				p.y += p.vy;
				p.rot += p.vr;
				ctx.save();
				ctx.globalAlpha = p.life > p.ttl - 26 ? Math.max(0, (p.ttl - p.life) / 26) : 1;
				ctx.translate(p.x, p.y);
				ctx.rotate(p.rot);
				ctx.fillStyle = p.color;
				if (p.shape === 'circle') { ctx.beginPath(); ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2); ctx.fill(); }
				else if (p.shape === 'ribbon') { ctx.fillRect(-p.size / 2, -p.size / 5, p.size, p.size / 2.6); }
				else { ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size); }
				ctx.restore();
			}
			if (alive > 0) {
				confettiRAF = requestAnimationFrame(frame);
			} else {
				confettiRAF = null;
				if (canvas && canvas.parentNode) { canvas.parentNode.removeChild(canvas); }
			}
		}
		if (confettiRAF) { cancelAnimationFrame(confettiRAF); }
		confettiRAF = requestAnimationFrame(frame);
	}

	/* ---------- countdown timer ---------- */
	function startTimer() {
		var timerEl = document.querySelector('.side-cart .timer');
		if (!timerEl) {
			if (timerInterval) { clearInterval(timerInterval); timerInterval = null; }
			return;
		}
		var mins = parseInt(timerEl.getAttribute('data-timer-minutes') || '10', 10) || 10;
		var until = parseInt(sessionStorage.getItem('cart_reserved_until') || '0', 10);
		var now = Date.now();
		if (!until || until < now) { until = now + mins * 60000; sessionStorage.setItem('cart_reserved_until', String(until)); }

		function tick() {
			var d = Math.max(0, Math.floor((until - Date.now()) / 1000));
			var m = Math.floor(d / 60), s = d % 60;
			var txt = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
			/* The countdown is cosmetic — write it INSIDE withoutObserver so it never trips the
			   MutationObserver. Otherwise every second it would force a full /cart.js sync +
			   discount re-render, replaying the chip's entrance animation (the visible blink). */
			withoutObserver(function () {
				var v = document.getElementById('cartTimerVal');
				var dp = document.getElementById('cartDispatchVal');
				if (v && v.textContent !== txt) { v.textContent = txt; }
				if (dp && dp.textContent !== txt) { dp.textContent = txt; }
			});
			if (d <= 0) { sessionStorage.removeItem('cart_reserved_until'); }
		}
		if (timerInterval) { clearInterval(timerInterval); }
		tick();
		timerInterval = setInterval(tick, 1000);
	}

	/* ---------- delegated handlers ---------- */
	/* ---------- discount helpers ---------- */
	function discMsg(msg, text, isErr) {
		if (!msg) { return; }
		msg.textContent = text || '';
		msg.classList.toggle('err', !!isErr);
		msg.classList.toggle('ok', !isErr && !!text);
	}
	function escAttr(s) {
		return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
	}
	function appliedCodes(cart) {
		return (cart.discount_codes || []).filter(function (d) { return d.applicable; }).map(function (d) { return d.code; });
	}
	function setDiscount(codesArr) {
		return fetch('/cart/update.js', {
			method: 'POST', headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ discount: codesArr.join(',') })
		}).then(function (r) { return r.json(); });
	}

	/* ---------- discount math ---------- */
	/* Format a percentage like "10%" or "12.5%" (blank when not meaningful). */
	function fmtPct(p) {
		if (!p || p <= 0) { return ''; }
		var v = Math.min(100, Math.round(p * 10) / 10);
		return (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)) + '%';
	}
	/* Per-code saving { amount(cents), pct, isPct } — resolved from cart-level applications
	   (matched by the entered code OR the discount's display title), then from line-level
	   allocations (for collection/product-targeted codes). Returns null when nothing matches. */
	function codeSaving(cart, code) {
		var up = String(code).toUpperCase();
		var base = cart.original_total_price || cart.items_subtotal_price || 0;
		function matches(a) {
			return a && (String(a.code || '').toUpperCase() === up || String(a.title || '').toUpperCase() === up);
		}
		/* 1) cart-level application */
		var app = (cart.cart_level_discount_applications || []).filter(matches)[0];
		if (app) {
			var amount = app.total_allocated_amount || 0;
			var isPct = app.value_type === 'percentage' && app.value != null;
			return { amount: amount, pct: isPct ? Number(app.value) : (base > 0 ? (amount / base * 100) : 0), isPct: isPct };
		}
		/* 2) line-level allocations (a code that targets specific products/collections) */
		var lineAmt = 0, lineIsPct = false, lineVal = null;
		(cart.items || []).forEach(function (it) {
			(it.line_level_discount_allocations || []).forEach(function (al) {
				if (al && matches(al.discount_application)) {
					lineAmt += al.amount || 0;
					var da = al.discount_application;
					if (da.value_type === 'percentage' && da.value != null) { lineIsPct = true; lineVal = Number(da.value); }
				}
			});
		});
		if (lineAmt > 0) {
			return { amount: lineAmt, pct: lineIsPct ? lineVal : (base > 0 ? (lineAmt / base * 100) : 0), isPct: lineIsPct };
		}
		return null;
	}

	/* Paint just the subtotal (struck pre-discount total + live discounted total = the checkout
	   amount). Split out so the MutationObserver can repaint it synchronously after the theme's
	   server re-render clobbers it on a quantity change. original_total_price == total_price + total_discount. */
	function paintSubtotal(cart) {
		var sub = document.querySelector('.side-cart .sub-total .total-price');
		if (!sub) { return; }
		/* The struck "was" + the "% OFF" describe the discount on the PAID merchandise only. Free-gift lines
		   must be excluded: a gift is 100% off, so folding it in both inflates the struck price (by the gift's
		   full price) and the discount, jumping e.g. 15% → 32% the moment a free shaker lands. So subtract the
		   gift's original value from `was`; `total_price` already excludes the gift (it's ₹0). The gift's value
		   is surfaced separately on its own "Free!" line and in "Saved so far". */
		var giftOriginal = giftLines(cart).reduce(function (s, it) { return s + (it.original_line_price || 0); }, 0);
		var origTotal = (typeof cart.original_total_price === 'number') ? cart.original_total_price : ((cart.total_price || 0) + (cart.total_discount || 0));
		var was = origTotal - giftOriginal;
		var now = cart.total_price || 0;
		var disc = was - now;   /* discount on the paid merchandise (order/tier discounts), gift value removed */
		if (disc > 0) {
			var pct = was > 0 ? Math.round(disc / was * 100) : 0;
			var html = '<span class="st-row"><s class="st-was">' + money(was) + '</s>'
				+ '<span class="st-now">' + money(now) + '</span></span>'
				+ (pct > 0 ? '<small class="st-off">(' + pct + '% OFF)</small>' : '');
			/* Only rewrite when the markup actually differs — re-setting identical innerHTML churns
			   the DOM each sync. (Differs after the theme clobbers it on a qty change → flash-fix.) */
			if (sub.innerHTML !== html) { sub.innerHTML = html; }
			sub.classList.add('has-disc');
		} else {
			var txt = money(now);
			if (sub.textContent !== txt) { sub.textContent = txt; }
			sub.classList.remove('has-disc');
		}
	}

	/* ---------- render applied discounts: chips + footer row + subtotal ---------- */
	function renderDiscounts(cart) {
		var codes = (cart.discount_codes || []).filter(function (d) { return d.applicable; });
		var disc = cart.total_discount || 0;
		var base = cart.original_total_price || cart.items_subtotal_price || 0;
		var overallPct = base > 0 ? (disc / base * 100) : 0;
		var apps = cart.cart_level_discount_applications || [];
		/* a single, genuine percentage discount → show its configured % (never a synthetic one) */
		var solePct = (apps.length === 1 && apps[0] && apps[0].value_type === 'percentage' && apps[0].value != null)
			? Number(apps[0].value) : 0;
		/* is any of total_discount from a non-code (automatic/script) application? if so, the whole
		   total_discount can't be attributed to a lone code in the fallback below. */
		var hasNonCodeDisc = apps.some(function (a) { return a && a.type && a.type !== 'discount_code'; });
		/* codes the reward engine owns (auto-applied via cookie) — not user-removable */
		var tierCodes = readTiers().filter(function (t) { return t.type === 'discount' && t.discount_code; })
			.map(function (t) { return String(t.discount_code).toUpperCase(); });

		/* keep the checkout-carryover cache in sync with the server truth */
		appliedDiscountCodes = codes.map(function (d) { return d.code; });

		var chips = document.querySelector('.side-cart [data-applied-discounts]');
		if (chips) {
			if (codes.length) {
				var html = codes.map(function (d) {
					var isTier = tierCodes.indexOf(String(d.code).toUpperCase()) > -1;
					var s = codeSaving(cart, d.code);
					if (!s && codes.length === 1 && disc > 0 && !hasNonCodeDisc) {
						s = { amount: disc, pct: solePct || overallPct, isPct: !!solePct };
					}
					var saveHtml;
					if (s && s.amount > 0) {
						var pctTxt = s.isPct ? fmtPct(s.pct) : '';
						saveHtml = '<span class="dchip-save">−' + money(s.amount)
							+ (pctTxt ? '<small>' + pctTxt + ' off</small>' : '') + '</span>';
					} else {
						saveHtml = '<span class="dchip-save dchip-save--ok">'
							+ '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>Applied</span>';
					}
					/* reward-tier codes get a lock badge instead of a remove control: the engine owns
					   them, and stripping one strands the tier (its one-shot guard blocks re-apply). */
					var tail = isTier
						? '<span class="dchip-lock" title="Earned reward — applied automatically" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>'
						: '<button type="button" class="dchip-x" data-disc-remove="' + escAttr(d.code) + '" aria-label="Remove discount ' + escAttr(d.code) + '">'
							+ '<svg class="dchip-x-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>'
							+ '<span class="dchip-x-spin" aria-hidden="true"></span></button>';
					return '<div class="dchip' + (isTier ? ' dchip--reward' : '') + '" data-disc-chip="' + escAttr(d.code) + '">'
						+ '<span class="dchip-ic" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m15 9-6 6"/><path d="M9 9h.01"/><path d="M15 15h.01"/></svg></span>'
						+ '<span class="dchip-main"><span class="dchip-code">' + escAttr(d.code) + '</span><span class="dchip-sub">' + (isTier ? 'Reward unlocked' : 'Discount applied') + '</span></span>'
						+ saveHtml
						+ tail
						+ '</div>';
				}).join('');
				/* Idempotent write: re-assigning identical innerHTML restarts the .dchip entrance
				   animation (the blink). Only touch the DOM when the set actually changed, or when a
				   server re-render swapped the container for a fresh node (chips !== lastChipsEl). */
				if (chips !== lastChipsEl || lastChipsSig !== html) {
					chips.innerHTML = html;
					lastChipsEl = chips;
					lastChipsSig = html;
				}
				chips.hidden = false;
			} else {
				if (chips !== lastChipsEl || lastChipsSig !== '') {
					chips.innerHTML = '';
					lastChipsEl = chips;
					lastChipsSig = '';
				}
				chips.hidden = true;
			}
		}

		/* Discount is surfaced as "(X% OFF)" beneath the subtotal (see paintSubtotal) — no separate row. */
		paintSubtotal(cart);
	}

	/* ---------- apply a code ---------- */
	/* core: apply a discount code. ctx = { input, msg, btn, onSuccess } — shared by the inline
	   discount field, the coupons-modal field, and per-coupon Apply buttons. */
	function applyCode(code, ctx) {
		ctx = ctx || {};
		var msg = ctx.msg, btn = ctx.btn, input = ctx.input;
		code = (code || '').trim();
		discMsg(msg, '', false);
		if (!code) { discMsg(msg, 'Enter a code', true); if (input) { input.focus(); } return; }
		if (BUSY) { return; }
		BUSY = true;
		if (btn) { btn.classList.add('loading'); btn.disabled = true; }
		var upper = code.toUpperCase();

		getCart().then(function (cart) {
			var existing = appliedCodes(cart);
			if (existing.some(function (c) { return c.toUpperCase() === upper; })) {
				return Promise.reject({ kind: 'dup' });
			}
			return setDiscount(existing.concat([code]));
		}).then(function (cart) {
			/* /cart/update.js returns the updated cart, so no extra round-trip needed */
			var hit = (cart.discount_codes || []).filter(function (d) { return d.code.toUpperCase() === upper; })[0];
			if (hit && hit.applicable) {
				if (input) { input.value = ''; }
				discMsg(msg, 'Discount applied', false);
				if (ctx.onSuccess) { try { ctx.onSuccess(code); } catch (e) {} }
				BUSY = false;   /* release before sync so tier reconciliation runs on this pass */
				return syncRewards();
			}
			/* recorded but not applicable -> roll back to the valid set + surface a clear error */
			return setDiscount(appliedCodes(cart)).then(function () {
				discMsg(msg, '“' + code + '” isn’t valid or doesn’t apply to this cart', true);
			});
		}).catch(function (e) {
			if (e && e.kind === 'dup') { discMsg(msg, 'That code is already applied', true); }
			else { discMsg(msg, 'Couldn’t apply that code — please try again', true); }
		}).finally(function () {
			BUSY = false;
			if (btn) { btn.classList.remove('loading'); btn.disabled = false; }
		});
	}

	function applyDiscount(btn) {
		var wrap = btn.closest('.disc');
		var input = wrap ? wrap.querySelector('[data-disc-input]') : null;
		var msg = wrap ? wrap.querySelector('[data-disc-msg]') : null;
		applyCode(input ? input.value : '', { input: input, msg: msg, btn: btn });
	}

	/* ---------- available-coupons modal ---------- */
	var couponsCloseTimer = null;
	function couponsMsgEl() { return document.querySelector('.side-cart [data-coupons-msg]'); }
	function syncCouponCards(m) {
		var applied = appliedDiscountCodes.map(function (c) { return String(c).toUpperCase(); });
		m.querySelectorAll('.npc-card').forEach(function (card) {
			var code = (card.getAttribute('data-coupon-card') || '').toUpperCase();
			var on = applied.indexOf(code) > -1;
			card.classList.toggle('is-applied', on);
			var btn = card.querySelector('[data-coupon-apply]');
			if (btn) { btn.classList.remove('loading'); btn.disabled = false; }
			var t = btn && btn.querySelector('.npc-apply-t');
			if (t) { t.textContent = on ? 'Applied' : 'Apply'; }
		});
	}
	function openCoupons() {
		var m = document.querySelector('.side-cart [data-coupons-modal]');
		if (!m) { return; }
		if (couponsCloseTimer) { clearTimeout(couponsCloseTimer); couponsCloseTimer = null; }
		discMsg(couponsMsgEl(), '', false);
		syncCouponCards(m);
		m.hidden = false;
		m.setAttribute('aria-hidden', 'false');
		/* two frames so the slide-up/scrim transitions actually run from their start state */
		requestAnimationFrame(function () { requestAnimationFrame(function () { m.classList.add('is-open'); }); });
	}
	function closeCoupons() {
		var m = document.querySelector('.side-cart [data-coupons-modal]');
		if (!m || m.hidden) { return; }
		m.classList.remove('is-open');
		m.setAttribute('aria-hidden', 'true');
		if (couponsCloseTimer) { clearTimeout(couponsCloseTimer); }
		couponsCloseTimer = setTimeout(function () { m.hidden = true; couponsCloseTimer = null; }, 520);
	}

	/* ---------- remove a code (spinner on the X until gone, then smooth collapse) ---------- */
	function removeDiscount(code, chip, btn) {
		if (BUSY || !code) { return; }
		BUSY = true;
		var upper = code.toUpperCase();
		/* reflect the intent immediately: drop it from the checkout-carryover cache now so a
		   checkout click during the (network + collapse) window can't re-carry the code. */
		var prevCodes = appliedDiscountCodes.slice();
		appliedDiscountCodes = appliedDiscountCodes.filter(function (c) { return c.toUpperCase() !== upper; });
		if (btn) { btn.classList.add('is-removing'); btn.disabled = true; }
		getCart().then(function (cart) {
			return setDiscount(appliedCodes(cart).filter(function (c) { return c.toUpperCase() !== upper; }));
		}).then(function () {
			/* collapse the chip out before the re-render swaps the list */
			return new Promise(function (resolve) {
				if (!chip) { return resolve(); }
				chip.classList.add('removing');
				var done = false, fin = function () { if (!done) { done = true; resolve(); } };
				chip.addEventListener('transitionend', fin, { once: true });
				setTimeout(fin, 340);
			});
		}).then(function () {
			discMsg(document.querySelector('.side-cart [data-disc-msg]'), '', false);
			BUSY = false;   /* release before sync so tier reconciliation runs on this pass */
			return syncRewards();
		}).catch(function () {
			appliedDiscountCodes = prevCodes;   /* removal failed — restore the cache */
			if (chip) { chip.classList.remove('removing'); }
			if (btn) { btn.classList.remove('is-removing'); btn.disabled = false; }
		}).finally(function () { BUSY = false; });
	}

	function toggleGiftwrap(btn) {
		if (BUSY) { return; }
		var vid = btn.getAttribute('data-variant');
		if (!vid) { return; }
		BUSY = true;
		btn.classList.add('loading');
		getCart().then(function (cart) {
			var line = (cart.items || []).filter(function (it) {
				return String(it.variant_id) === String(vid) || (it.properties && it.properties._giftwrap);
			})[0];
			if (line) {
				return fetch('/cart/change.js', {
					method: 'POST', headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ id: line.key, quantity: 0 })
				});
			}
			return fetch('/cart/add.js', {
				method: 'POST', headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ items: [{ id: Number(vid), quantity: 1, properties: { _giftwrap: 'true' } }] })
			});
		}).then(function () { return rerenderDrawer(); })
			.catch(function () {})
			.finally(function () { BUSY = false; btn.classList.remove('loading'); });
	}

	function toggleSub(el) {
		if (BUSY) { return; }
		var plan = el.getAttribute('data-plan');
		var key = el.getAttribute('data-key');
		if (!plan || !key) { return; } // no selling plan on this product -> no-op (documented)
		var isOn = el.classList.contains('on');
		BUSY = true;
		el.classList.toggle('on');
		fetch('/cart/change.js', {
			method: 'POST', headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: key, selling_plan: isOn ? null : Number(plan) })
		}).then(function () { return rerenderDrawer(); })
			.catch(function () {})
			.finally(function () { BUSY = false; });
	}

	/* ---------- bundle upsell: one-tap add of the missing pack size ----------
	   The 10% itself is a Shopify automatic discount; this just adds the second variant so the bundle
	   condition is met. Holds BUSY across the whole add+rerender, then schedules a follow-up sync so any
	   reward-tier reconcile (cart total changed) runs cleanly once the lock is free. */
	function puAdd(btn) {
		if (BUSY || !btn) { return; }
		var id = btn.getAttribute('data-pu-add');
		if (!id) { return; }
		/* Upgrade ladder: data-pu-swap lists the lower pack(s) (comma-separated variant ids) to REMOVE when this
		   upgrade is added — e.g. adding the 4-month (120) removes the 60-cap; adding the 6-month removes both.
		   The swap fires only on this upsell tap (not on any other add), matching the chosen behaviour. */
		var swap = (btn.getAttribute('data-pu-swap') || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
		BUSY = true;
		btn.classList.remove('pu-add-err');
		btn.classList.add('loading');
		var JH = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
		/* ATOMIC: add the pack, capture its render, then settle the free gift and paint ONCE (count + shaker
		   together) BEFORE the loader clears — so the instant the button stops spinning the gift is already in.
		   No intermediate count-only paint, no ~600ms pop-in. */
		var finalHtml = null;
		fetch('/cart/add.js', {
			method: 'POST', headers: JH,
			body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections: ['side-cart'], sections_url: window.location.pathname })
		}).then(function (r) {
			/* /cart/add.js returns 422 with a JSON reason (sold out / not published / unavailable). Surface it
			   rather than rejecting a bare status, so the console shows WHY and the loader never just hangs. */
			if (!r.ok) { return r.json().catch(function () { return { description: 'HTTP ' + r.status }; }).then(function (e) { return Promise.reject(e); }); }
			return r.json();
		}).then(function (res) {
			finalHtml = res && res.sections && res.sections['side-cart'];
			if (!swap.length) { return; }
			/* remove the lower pack(s) by variant id (set qty 0 via /cart/update.js — a no-op for ones not in
			   cart); the swap response is the freshest post-swap render. */
			var updates = {};
			swap.forEach(function (v) { updates[v] = 0; });
			return fetch('/cart/update.js', {
				method: 'POST', headers: JH,
				body: JSON.stringify({ updates: updates, sections: ['side-cart'], sections_url: window.location.pathname })
			}).then(function (r2) { return r2.ok ? r2.json() : res; })
				.then(function (res2) { finalHtml = (res2 && res2.sections && res2.sections['side-cart']) || finalHtml; });
		}).then(function () {
			/* release the add lock, then reconcile + paint atomically from the fresh cart (single paint) */
			BUSY = false;
			return getCart().then(function (cart) { lastCart = cart; return reconcileAndPaint(cart, finalHtml); });
		}).catch(function (err) {
				try { console.warn('[cart upsell] could not add variant ' + id + ' — ' + ((err && (err.description || err.message)) || err) + '. Check the recommended product is Active, published to the Online Store sales channel, and in stock.'); } catch (e) {}
				btn.classList.add('pu-add-err');
				setTimeout(function () { if (btn) { btn.classList.remove('pu-add-err'); } }, 2200);
			})
			/* clear the loader + lock LAST — by now the gift has landed, so "loader gone" == "gift in cart" */
			.finally(function () { BUSY = false; btn.classList.remove('loading'); });
	}

	/* Manual free-gift claim: the shopper tapped "Add" on the unlocked gift card. Arm the reconciler and run it
	   now so the gift lands immediately (reconcileTiers honours puGiftArm and adds up to the earned quantity). */
	function puGiftAdd(btn) {
		if (!btn) { return; }
		/* which gift rule? data-pu-gift-add carries the reward variant id (the key); '' falls back to '1' so a
		   legacy single-gift card still works. */
		var key = btn.getAttribute('data-pu-gift-add') || '1';
		/* Ignore if THIS rule's claim is already armed/in-flight — de-dupes fast double-taps (arm set + consumed
		   synchronously inside reconcileGift, so a second tap can never launch a second add). */
		if (puGiftArm[key] || puGiftClaiming[key]) { return; }
		/* This gift was already shown to strip a customer discount → never re-arm a claim that reconcileGift
		   will refuse (which would spin forever). Flash the error state instead and bail. */
		if (puGiftConflict[key] === true) {
			btn.classList.remove('loading'); btn.classList.add('pu-add-err');
			setTimeout(function () { if (btn) { btn.classList.remove('pu-add-err'); } }, 2200);
			return;
		}
		puGiftArm[key] = true;          // arm the reconciler to add up to the earned quantity
		puGiftClaiming[key] = true;     // the spinner + any error are now driven by syncGiftPop() off each render
		btn.classList.remove('pu-add-err');
		btn.classList.add('loading');
		/* Fire the reconcile. If the cart is momentarily BUSY, reconcileTiers no-ops this pass but the arm persists
		   and syncRewards re-schedules itself until the gift lands — so the tap is never dropped, and the spinner
		   stays on the (re-rendered) button the whole time because puGiftClaiming is read on every paint. */
		syncRewards();
	}

	/* ---------- order-note sheet helpers ---------- */
	function noteCloseSheet() {
		var p = document.querySelector('.side-cart .popup-toolDown.open');
		if (p) { p.classList.remove('open'); }
		var o = document.querySelector('.side-cart .mini-cart-overlay.open');
		if (o) { o.classList.remove('open'); }
	}
	function noteUpdateCount(area) {
		if (!area) { return; }
		var sheet = area.closest('.note-sheet');
		if (!sheet) { return; }
		var max = parseInt(area.getAttribute('maxlength') || '500', 10) || 500;
		var len = area.value.length;
		var cEl = sheet.querySelector('[data-note-count]');
		var wrap = sheet.querySelector('[data-note-counter]');
		if (cEl) { cEl.textContent = len; }
		if (wrap) { wrap.classList.toggle('near', len >= max - 50); }
	}
	function noteInit() {
		var area = document.querySelector('.side-cart [data-note-area]');
		if (area) { noteUpdateCount(area); }
	}
	/* Save the order note ourselves (replaces the theme's instant-close cart-note handler) so we
	   can show a loading spinner + an explicit "Note added" confirmation before the sheet closes. */
	function noteSave(btn) {
		if (!btn || btn.classList.contains('loading') || btn.classList.contains('saved')) { return; }
		var sheet = btn.closest('.note-sheet');
		var area = sheet ? sheet.querySelector('[data-note-area]') : null;
		if (!area) { return; }
		var tx = btn.querySelector('.note-save-tx');
		var note = area.value;
		btn.classList.remove('error');
		btn.classList.add('loading');
		btn.setAttribute('aria-busy', 'true');
		var url = (window.btyGlobals && btyGlobals.cart_update_url) || '/cart/update.js';
		fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
			body: JSON.stringify({ note: note })
		}).then(function (r) {
			if (!r.ok) { throw new Error('save failed'); }
			return r.json();
		}).then(function () {
			lastCart = null;
			btn.classList.remove('loading');
			btn.classList.add('saved');
			btn.removeAttribute('aria-busy');
			if (tx) { tx.textContent = note.trim() ? 'Note added' : 'Note cleared'; }
			setTimeout(function () {
				noteCloseSheet();
				setTimeout(function () { btn.classList.remove('saved'); if (tx) { tx.textContent = 'Save note'; } }, 450);
			}, 950);
		}).catch(function () {
			btn.classList.remove('loading');
			btn.classList.add('error');
			btn.removeAttribute('aria-busy');
			if (tx) { tx.textContent = 'Try again'; }
			setTimeout(function () { btn.classList.remove('error'); if (tx) { tx.textContent = 'Save note'; } }, 1800);
		});
	}

	/* live character counter */
	document.addEventListener('input', function (e) {
		var a = e.target;
		if (a && a.matches && a.matches('.side-cart [data-note-area]')) { noteUpdateCount(a); }
	});

	/* Close the side cart the same way the theme does (remove the html class, restore focus,
	   announce it) — but callable from our delegated handler so it keeps working after a re-render. */
	function closeSideCart() {
		document.documentElement.classList.remove('side-cart-open');
		var sc = document.querySelector('.side-cart');
		try { if (sc && sc._lastFocused && sc._lastFocused.focus) { sc._lastFocused.focus(); } } catch (e) {}
		try { document.dispatchEvent(new CustomEvent('theme-popup-close')); } catch (e) {}
	}

	document.addEventListener('click', function (e) {
		var t;
		if (!e.target.closest('.side-cart')) { return; }
		/* click the dimmed scrim (anywhere outside the sheet) → close the note sheet */
		if (e.target.closest('.mini-cart-overlay')) { noteCloseSheet(); return; }
		if ((t = e.target.closest('[data-disc-toggle]'))) { var d = t.closest('.disc'); if (d) { d.classList.toggle('open'); } return; }
		if ((t = e.target.closest('[data-cart-discount-apply]'))) { applyDiscount(t); return; }
		if (e.target.closest('[data-coupons-open]')) { openCoupons(); return; }
		if (e.target.closest('[data-coupons-close]')) { closeCoupons(); return; }
		if ((t = e.target.closest('[data-coupon-apply]'))) {
			var card = t.closest('.npc-card');
			applyCode(t.getAttribute('data-coupon-apply'), {
				btn: t, msg: couponsMsgEl(),
				onSuccess: function () { if (card) { card.classList.add('is-applied'); var ct = t.querySelector('.npc-apply-t'); if (ct) { ct.textContent = 'Applied'; } } setTimeout(closeCoupons, 700); }
			});
			return;
		}
		if ((t = e.target.closest('[data-coupons-apply-input]'))) {
			var sheet = t.closest('.npc-sheet');
			var cInp = sheet ? sheet.querySelector('[data-coupons-input]') : null;
			applyCode(cInp ? cInp.value : '', {
				input: cInp, btn: t, msg: couponsMsgEl(),
				onSuccess: function () { setTimeout(closeCoupons, 700); }
			});
			return;
		}
		if ((t = e.target.closest('[data-disc-remove]'))) { removeDiscount(t.getAttribute('data-disc-remove'), t.closest('.dchip'), t); return; }
		if ((t = e.target.closest('[data-giftwrap]'))) { toggleGiftwrap(t); return; }
		/* Save the note (loader → "Note added" confirmation → close) — we own this now */
		if ((t = e.target.closest('[data-update-note]'))) { noteSave(t); return; }
		if ((t = e.target.closest('.fbt-card .fadd'))) { if (!t.classList.contains('is-soldout')) { t.classList.add('added'); } return; }
		if ((t = e.target.closest('.cc-card .cc-add'))) { if (!t.classList.contains('is-soldout')) { t.classList.add('added'); } return; }
		/* Close the drawer ourselves via delegation. The theme binds its close handler to a SPECIFIC
		   button element on open (btyClosePopup), but applyDrawerHtml() replaces .side-cart-inner's
		   innerHTML after a remove/empty re-render — destroying that element so the native handler dies
		   and the X stops closing once the cart empties. Delegation survives every re-render. */
		if ((t = e.target.closest('.side-cart-close,[data-side-cart-close]'))) { closeSideCart(); return; }
		if ((t = e.target.closest('[data-pu-gift-add]'))) { puGiftAdd(t); return; }
		if ((t = e.target.closest('[data-pu-add]'))) { puAdd(t); return; }
		if ((t = e.target.closest('[data-sub]'))) { toggleSub(t); return; }
	});

	/* Remove: slide-out before the theme's fetch resolves. */
	/* Esc closes the coupons modal; Enter applies from either discount input */
	document.addEventListener('keydown', function (e) {
		if (e.key === 'Escape') {
			var m = document.querySelector('.side-cart [data-coupons-modal]');
			if (m && !m.hidden) { closeCoupons(); }
			return;
		}
		if (e.key !== 'Enter') { return; }
		var cInp = e.target.closest && e.target.closest('[data-coupons-input]');
		if (cInp) {
			e.preventDefault();
			var sheet = cInp.closest('.npc-sheet');
			applyCode(cInp.value, {
				input: cInp, btn: sheet ? sheet.querySelector('[data-coupons-apply-input]') : null,
				msg: couponsMsgEl(), onSuccess: function () { setTimeout(closeCoupons, 700); }
			});
			return;
		}
		var inp = e.target.closest && e.target.closest('[data-disc-input]');
		if (!inp) { return; }
		e.preventDefault();
		var apply = inp.closest('.disc').querySelector('[data-cart-discount-apply]');
		if (apply) { applyDiscount(apply); }
	});

	/* ---------- carry the applied discount into checkout ----------
	   A manual /cart/update.js code lives in the server cart but can desync at the checkout
	   handoff (the customer then sees the undiscounted total). Shopify's checkout ?discount=
	   URL param reliably re-asserts it — but it honors exactly ONE code (comma-separated values
	   are read as a single literal code and match nothing), and automatic discounts re-apply at
	   checkout on their own. So we ONLY divert when there is a single user-entered code; for 0
	   codes, 2+ stacked codes, or reward-tier codes (carried by their own cookie), the native
	   form POST proceeds — the server cart already holds every code + automatic discount. */
	document.addEventListener('submit', function (e) {
		var form = e.target && e.target.closest && e.target.closest('.side-cart .cart-checkout-form');
		if (!form) { return; }
		/* a discount add/remove is in flight — the server cart is mid-update, so don't divert with a
		   possibly-indeterminate code set; let the native POST proceed against the settled server cart. */
		if (BUSY) { return; }
		var tierCodes = readTiers().filter(function (t) { return t.type === 'discount' && t.discount_code; })
			.map(function (t) { return String(t.discount_code).toUpperCase(); });
		var manual = appliedDiscountCodes.filter(function (c) { return tierCodes.indexOf(String(c).toUpperCase()) === -1; });
		if (manual.length !== 1) { return; }   /* native POST handles 0 / 2+ / tier-only carts */
		e.preventDefault();
		var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
		if (root.charAt(root.length - 1) !== '/') { root += '/'; }
		window.location.href = root + 'checkout?discount=' + encodeURIComponent(manual[0]);
	});

	/* ---------- smooth item removal: loader → swipe-right + collapse → re-render ---------- */
	function removeItem(itemEl) {
		if (!itemEl || itemEl.classList.contains('deleting') || itemEl.classList.contains('removing')) { return; }
		if (BUSY) { return; }
		var line = parseInt(itemEl.getAttribute('data-line'), 10);
		if (!line) { return; }
		BUSY = true;
		var emptied = false, freshHtml = null, cart = null;
		itemEl.classList.add('deleting');   /* spinner on the trash button while the request is in flight */
		/* ask /cart/change.js to return the freshly-rendered side-cart section IN THE SAME RESPONSE —
		   it reflects the exact post-delete cart, so there's no stale/cached second fetch. */
		fetch('/cart/change.js', {
			method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
			body: JSON.stringify({ line: line, quantity: 0, sections: ['side-cart'], sections_url: window.location.pathname })
		}).then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
			.then(function (res) {
				cart = res;                                               /* /cart/change.js returns the full post-delete cart */
				emptied = !(res && res.item_count);                       /* cart empty → "continue shopping" view */
				freshHtml = res && res.sections && res.sections['side-cart'];
				/* removed server-side → play the swipe-right + collapse (collapse glides the items below up) */
				return new Promise(function (resolve) {
					itemEl.classList.remove('deleting');
					itemEl.style.height = itemEl.offsetHeight + 'px';
					void itemEl.offsetHeight;            /* commit the start height before transitioning */
					itemEl.classList.add('removing');
					itemEl.style.height = '0px';
					var done = false, fin = function () { if (!done) { done = true; resolve(); } };
					itemEl.addEventListener('transitionend', function (ev) { if (ev.target === itemEl && ev.propertyName === 'height') { fin(); } });
					setTimeout(fin, 460);                /* fallback in case transitionend is missed */
				});
			})
			.then(function () {
				BUSY = false;   /* release so reconcileTiers can run its op chain inside reconcileAndPaint */
				/* Cart now empty: paint the "continue shopping" view + zero the navbar badge directly (no gift
				   reconcile on an empty cart; .cart-item-count lives OUTSIDE .side-cart-inner so the re-render
				   never touched it). Otherwise settle ATOMICALLY — the removed line AND any now-unearned free gift
				   leave together in ONE paint (no "gift lingers ~600ms then vanishes" gap), for creatine or C+T alike. */
				if (emptied) {
					if (!applyDrawerHtml(freshHtml)) { return rerenderDrawer(); }
					syncHeaderCount(0);
					return;
				}
				return reconcileAndPaint(cart, freshHtml);
			})
			.catch(function () {
				itemEl.classList.remove('deleting', 'removing');
				itemEl.style.height = '';
				BUSY = false;
			});
	}
	/* Intercept the remove click in the CAPTURE phase so it runs before — and replaces — the theme's
	   onclick remove; preventDefault + stopImmediatePropagation hand the whole flow to removeItem. */
	document.addEventListener('click', function (e) {
		var rm = e.target.closest && e.target.closest('.side-cart .product-remove');
		if (!rm) { return; }
		e.preventDefault();
		e.stopImmediatePropagation();
		removeItem(rm.closest('.product-item'));
	}, true);
	/* Own the qty +/- so the free gift lands/leaves ATOMICALLY with the count — ONE final paint, no pop-in.
	   The theme's own handler re-renders the count first, then our observer would add/remove the gift a beat
	   later (the ~600ms pop-in the shopper sees). We capture the click (like removeItem), run /cart/change.js
	   ourselves, then reconcileAndPaint paints count + shaker together. While another write is in flight, the
	   BUSY guard swallows the tap (parity with the old block). */
	function stepQty(qb) {
		if (BUSY) { return; }
		var item = qb.closest && qb.closest('.product-item');
		if (!item) { return; }
		var line = parseInt(item.getAttribute('data-line'), 10);
		if (!line) { return; }
		var input = item.querySelector('.quantity-input');
		var cur = input ? (parseInt(input.value, 10) || parseInt(input.getAttribute('data-qty'), 10) || 0) : 0;
		var next = cur + (qb.getAttribute('name') === 'plus' ? 1 : -1);
		if (next < 0) { return; }
		BUSY = true;
		item.classList.add('np-qty-busy');   /* stepper spinner until the single final paint swaps the DOM */
		var JH = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
		var pendingHtml = null;
		fetch('/cart/change.js', {
			method: 'POST', headers: JH,
			body: JSON.stringify({ line: line, quantity: next, sections: ['side-cart'], sections_url: window.location.pathname })
		}).then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
			.then(function (cart) {
				lastCart = cart;
				pendingHtml = cart.sections && cart.sections['side-cart'];
				BUSY = false;   /* release so reconcileTiers can run its op chain inside reconcileAndPaint */
				if (!cart.item_count) {                                   /* stepped the last unit → cart empty */
					if (pendingHtml) { applyDrawerHtml(pendingHtml); } else { rerenderDrawer(); }
					syncHeaderCount(0);
					return;
				}
				return reconcileAndPaint(cart, pendingHtml);            /* count + gift → one paint */
			})
			.catch(function () { return rerenderDrawer(); })
			.finally(function () { BUSY = false; item.classList.remove('np-qty-busy'); });   /* the paint usually replaces the node; clear here too in case it didn't */
	}
	document.addEventListener('click', function (e) {
		var qb = e.target.closest && e.target.closest('.side-cart .quantity-button');
		if (!qb) { return; }
		e.preventDefault();
		e.stopImmediatePropagation();
		stepQty(qb);
	}, true);

	/* ---------- hide floating chat/WhatsApp widgets while the drawer is open ----------
	   The scrim uses max z-index, but app-injected FABs (e.g. the Ezy WhatsApp chat
	   button) often use max z-index too and win on DOM order, painting over the drawer.
	   Class/id selectors can't catch every app, so we find the widget the robust way —
	   by its wa.me/whatsapp link or by being a small fixed button in the bottom-right —
	   and hide it while the cart is open, restoring it on close. */
	var cfWatch = null, cfDebounce = null, cfTimers = [];
	function cfFixedAncestor(node) {
		while (node && node !== document.body) {
			var pos = ''; try { pos = getComputedStyle(node).position; } catch (e) {}
			if (pos === 'fixed') { return node; }
			node = node.parentElement;
		}
		return null;
	}
	function cfCollect(deep) {
		var found = [];
		function add(el) { if (el && found.indexOf(el) === -1) { found.push(el); } }
		/* (a) WhatsApp by link / class / id → its fixed container (cheap, targeted — the Ezy FAB carries a
		   wa.me link, so this alone catches it; run on every re-check). Skip links that live inside our OWN
		   surfaces (drawer / sticky-atc / product-video) so we never over-hide a theme WhatsApp SHARE link. */
		var kw = document.querySelectorAll('a[href*="wa.me"],a[href*="wa.link"],a[href*="whatsapp" i],[class*="whatsapp" i],[id*="whatsapp" i],iframe[src*="whatsapp" i]');
		for (var i = 0; i < kw.length; i++) {
			if (kw[i].closest('.side-cart') || kw[i].closest('[data-np-sticky-atc]') || kw[i].closest('[data-np-pvideo]')) { continue; }
			var f = cfFixedAncestor(kw[i]); if (f) { add(f); }
		}
		/* (b) any small fixed button stacked along the bottom-right (chat / FAB / back-to-top). EXPENSIVE
		   (getComputedStyle over many nodes), so only on the bounded `deep` passes — not the frequent observer. */
		if (deep) {
			var vw = window.innerWidth, vh = window.innerHeight;
			var cands = document.querySelectorAll('div,a,button,aside,iframe');
			for (var j = 0; j < cands.length; j++) {
				var el = cands[j];
				if (el.closest('.side-cart') || el.closest('[data-np-sticky-atc]') || el.closest('[data-np-pvideo]')) { continue; }
				var cs; try { cs = getComputedStyle(el); } catch (e) { continue; }
				if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') { continue; }
				var r = el.getBoundingClientRect();
				if (r.width < 24 || r.height < 24 || r.width > 160 || r.height > 160) { continue; }   /* button-sized */
				if ((vw - r.right) > 80 || (vh - r.bottom) > 300) { continue; }                        /* bottom-right zone */
				add(el);
			}
		}
		/* keep only the OUTERMOST element of each widget */
		return found.filter(function (el) { return !found.some(function (o) { return o !== el && o.contains(el); }); });
	}
	/* Tag each collected floater so CSS hides it while an overlay class (side-cart-open / npcu-lock) is on
	   <html>. We DON'T touch inline styles: two independent overlays managing the same element's inline
	   visibility collide — one's restore clobbers the other (the exact mobile bug where opening the checkout
	   sheet drops side-cart-open, whose restore un-hid the FAB the sheet had just hidden). A stable data-attr
	   + CSS reference-counts via class presence instead: the FAB hides while EITHER overlay is open and returns
	   only when BOTH are closed. The tag is inert with no overlay open, so it never needs removing. Idempotent. */
	function cfTagNow(deep) {
		cfCollect(deep).forEach(function (el) { el.setAttribute('data-np-hide-floater', '1'); });
	}
	function cfSetHidden(on) {
		if (on) {
			cfTagNow(true);
			/* The Ezy WhatsApp chat FAB (and similar apps) inject / re-mount their button ASYNCHRONOUSLY —
			   often AFTER the drawer already opened (especially on first page load), so the one-shot tag above
			   misses it and it paints over the drawer. Keep enforcing while open: re-tag on DOM insertions
			   (debounced, cheap link-only scan) + a few delayed deep ticks, so a late FAB can't slip through. */
			if ('MutationObserver' in window && !cfWatch) {
				cfWatch = new MutationObserver(function () {
					if (cfDebounce) { return; }
					cfDebounce = setTimeout(function () { cfDebounce = null; cfTagNow(false); }, 200);
				});
				try { cfWatch.observe(document.body, { childList: true, subtree: true }); } catch (e) {}
			}
			cfTimers = [250, 800, 1600, 3000].map(function (t) { return setTimeout(function () { cfTagNow(true); }, t); });
		} else {
			/* Just stop watching — CSS un-hides automatically once side-cart-open leaves <html> (unless the
			   checkout-upsell sheet is still open, in which case its npcu-lock rule keeps the FAB hidden). */
			if (cfWatch) { cfWatch.disconnect(); cfWatch = null; }
			if (cfDebounce) { clearTimeout(cfDebounce); cfDebounce = null; }
			cfTimers.forEach(function (t) { clearTimeout(t); }); cfTimers = [];
		}
	}

	/* ---------- "you may also like" carousel ----------
	   The container renders empty on a normal page load (Shopify's `recommendations` object is
	   only populated when the side-cart section is fetched via /recommendations/products). We
	   fetch that endpoint once, extract the rendered carousel, and inject it. Re-runs after a
	   server re-render (which returns the empty container again). */
	function loadCarousel() {
		var box = document.querySelector('.side-cart [data-cart-carousel]');
		/* Skip if already loaded with REAL cards (skeleton cards don't count — they're the placeholder
		   the server renders so the row reserves its height until the real cards swap in). */
		if (!box || box.getAttribute('data-cc-loaded') === '1' || box.querySelector('.cc-card:not(.cc-card--skel)')) { return; }
		var url = box.getAttribute('data-cc-url');
		if (!url) { return; }
		box.setAttribute('data-cc-loaded', '1');
		fetch(url, { headers: { 'Accept': 'text/html' } })
			.then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
			.then(function (html) {
				var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-carousel]');
				if (fresh && fresh.querySelector('.cc-card')) {
					withoutObserver(function () { box.innerHTML = fresh.innerHTML; });   /* swap skeleton → real cards */
					if (window.btyAddToCart) { try { btyAddToCart(box); } catch (e) {} }
				} else {
					/* no recommendations — drop the skeleton so .cc-wrap:empty collapses it (no permanent skeleton) */
					withoutObserver(function () { box.innerHTML = ''; });
				}
			})
			.catch(function () { box.removeAttribute('data-cc-loaded'); });   /* transient error — keep the skeleton, allow a retry */
	}

	/* When the theme's FBT recommendation fetch finishes (it fires product-card-updated), collapse the
	   FBT if it came back with no recommendation — otherwise its skeleton would show forever. A later
	   re-render re-evaluates this. The skeleton itself is hidden by CSS as soon as a real card lands. */
	document.addEventListener('product-card-updated', function () {
		var recs = document.querySelector('.side-cart .minicart-recommendations[data-source]');
		if (!recs) { return; }
		var wrap = recs.closest('.minicart-recommendations-wrapper');
		if (wrap) { wrap.classList.toggle('np-recs-empty', !recs.querySelector('.fbt-card')); }
	});

	/* ---------- init ---------- */
	/* ---------- rotating social proof ---------- */
	/* Re-queries the DOM each tick so it survives the theme's server re-renders (the header is
	   replaced on cart changes). Finds the active line, advances to the next, wrapped in
	   withoutObserver so the class toggle doesn't trigger a cart re-sync. */
	function proofTick() {
		var proof = document.querySelector('.side-cart [data-proof]');
		if (!proof || proof.getAttribute('data-mode') !== 'rotate') { return; }
		var items = proof.querySelectorAll('.proof-item');
		if (items.length < 2) { return; }
		var cur = 0;
		items.forEach(function (it, i) { if (it.classList.contains('is-active')) { cur = i; } });
		withoutObserver(function () {
			items[cur].classList.remove('is-active');
			items[(cur + 1) % items.length].classList.add('is-active');
		});
	}
	function startProof() {
		if (proofInterval) { return; }
		var proof = document.querySelector('.side-cart [data-proof]');
		if (!proof || proof.getAttribute('data-mode') !== 'rotate') { return; }
		if (proof.querySelectorAll('.proof-item').length < 2) { return; }
		var delay = (parseFloat(proof.getAttribute('data-delay')) || 3) * 1000;
		proofInterval = setInterval(proofTick, delay);
	}

	function init() {
		var inner = document.querySelector('.side-cart-inner');
		lastCrossed = document.querySelectorAll('#cart-reward .mk.hit').length;
		/* seed the free-shaker earned state from server truth so an already-earned cart on load never
		   fires a stale celebration on the first re-render (parity with lastCrossed above) */
		lastRewardEarned = !!document.querySelector('#cart-reward [data-reward-gift-mk].rw-hit');
		/* Seed the checkout-carryover cache from server truth so a fast checkout click before
		   the first /cart.js resolves still carries an already-applied code. */
		var seedEl = document.querySelector('.side-cart [data-applied-discounts]');
		var seed = seedEl && seedEl.getAttribute('data-initial-codes');
		if (seed) {
			appliedDiscountCodes = seed.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
		}
		startTimer();
		startProof();
		/* seed the one-shot pop guard from server truth: if an "unlocked" gift card is ALREADY rendered at load
		   (e.g. a reload while earned-but-unclaimed), record it as already-shown so the pop only fires on a
		   genuine in-session unlock, not on this load or the next unrelated re-render. */
		document.querySelectorAll('.side-cart .pu-gift.is-done').forEach(function (c) { puWasDone[c.getAttribute('data-gift-key') || '1'] = true; });
		syncRewards();
		noteInit();
		loadCarousel();
		if (inner && 'MutationObserver' in window) {
			observer = new MutationObserver(function () {
				/* The theme re-renders `.side-cart .sub-total` from server Liquid on a quantity
				   change; Liquid can't see manual codes, so it momentarily paints the UNDISCOUNTED
				   total. Repaint the discounted subtotal synchronously (this observer runs as a
				   microtask, before the browser paints) to kill the flash. scheduleSync then
				   reconciles to the exact new total from /cart.js. */
				if (lastCart && (lastCart.total_discount || 0) > 0) {
					var sub = document.querySelector('.side-cart .sub-total .total-price');
					if (sub && !sub.classList.contains('has-disc')) {
						withoutObserver(function () { paintSubtotal(lastCart); });
					}
				}
				/* Kill the transient "paid shaker" flash on a qty reduce — runs as a microtask before paint, so
				   the over-target/charged gift unit is hidden the instant the theme re-renders it; the reconciler
				   (scheduleSync below) then removes it server-side. Toggling a class doesn't re-fire this observer
				   (it watches childList/subtree, not attributes), so no loop. */
				hidePendingPaidGift();
				syncGiftPop();   /* the theme just re-rendered the drawer — pop the gift checkmark if it newly appeared */
				scheduleSync();
				/* The theme's add-to-cart re-renders .side-cart-inner, bringing the carousel back as a
				   skeleton — refill it (guarded: no-ops once real cards are present). The FBT is refilled
				   by the theme's own btyMinicartRecommendations; its skeleton hides via CSS once filled. */
				loadCarousel();
			});
			observer.observe(inner, { childList: true, subtree: true });
		}
		/* Re-sync + restart timer only on the open transition (not every class change). */
		if ('MutationObserver' in window) {
			var wasOpen = document.documentElement.classList.contains('side-cart-open');
			new MutationObserver(function () {
				var isOpen = document.documentElement.classList.contains('side-cart-open');
				if (isOpen && !wasOpen) { startTimer(); startProof(); scheduleSync(); cfSetHidden(true); loadCarousel(); }
				else if (!isOpen && wasOpen) { cfSetHidden(false); }
				wasOpen = isOpen;
			}).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
